/**
 * Validates DocumentLayoutV1 for §14 UStG mandatory fields and geometric bounds.
 * Shared by client (editor save gate) and server (PUT handler).
 */
import {
  FOOTER_ELEMENT_KEYS,
  HEADER_ELEMENT_KEYS,
  LAYOUT_FIELD_DEFS,
  MANDATORY_FOOTER_KEYS,
  MANDATORY_HEADER_KEYS,
  PDF_A4_HEIGHT_PT,
  PDF_A4_WIDTH_PT,
  type DocumentLayoutV1,
  type LayoutElement,
  type LayoutElementKey,
  isDocumentLayoutV1,
  minElementHeightPt,
  minElementWidthPt,
} from './documentLayoutTypes';

export type LayoutValidationIssue = {
  code:
    | 'invalid_shape'
    | 'invalid_page_margins'
    | 'missing_mandatory'
    | 'out_of_bounds'
    | 'invalid_band_height'
    | 'unknown_key'
    | 'duplicate_mandatory';
  message: string;
  elementId?: string;
  key?: LayoutElementKey;
};

export type LayoutValidationResult = {
  ok: boolean;
  issues: LayoutValidationIssue[];
};

const HEADER_KEY_SET = new Set<string>(HEADER_ELEMENT_KEYS);
const FOOTER_KEY_SET = new Set<string>(FOOTER_ELEMENT_KEYS);

function elementLabel(key: string): string {
  const def = LAYOUT_FIELD_DEFS.find((d) => d.key === key);
  return def?.label ?? key;
}

function hasKey(elements: LayoutElement[], key: LayoutElementKey): boolean {
  return elements.some((e) => e.key === key);
}

/** freeText may cover a labeled field via `{{token}}` after merge. */
function freeTextCoversToken(elements: LayoutElement[], token: string): boolean {
  const needle = `{{${token}}}`;
  return elements.some((e) => e.key === 'freeText' && (e.text ?? '').includes(needle));
}

function hasFieldOrTokenCoverage(elements: LayoutElement[], key: LayoutElementKey): boolean {
  if (hasKey(elements, key)) return true;
  // documentTitle value in labeled form is the invoice number.
  if (key === 'documentTitle') {
    return freeTextCoversToken(elements, 'documentTitle') || freeTextCoversToken(elements, 'invoiceNumber');
  }
  return freeTextCoversToken(elements, key);
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function elementOutOfBounds(
  el: LayoutElement,
  bandHeightPt: number,
): boolean {
  if (!isFiniteNumber(el.xPt) || !isFiniteNumber(el.yPt) || !isFiniteNumber(el.wPt) || !isFiniteNumber(el.hPt)) {
    return true;
  }
  if (el.wPt < minElementWidthPt(el.key) || el.hPt < minElementHeightPt(el.key)) return true;
  if (el.xPt < 0 || el.yPt < 0) return true;
  if (el.xPt + el.wPt > PDF_A4_WIDTH_PT + 0.5) return true;
  if (el.yPt + el.hPt > bandHeightPt + 0.5) return true;
  return false;
}

/**
 * Document title already carries the invoice number in the legacy layout, so
 * either `documentTitle` OR `invoiceNumber` (header or footer, field or token)
 * satisfies the number requirement.
 */
function hasInvoiceNumberCoverage(layout: DocumentLayoutV1): boolean {
  const header = layout.header.elements;
  const footer = layout.footer.elements;
  return (
    hasKey(header, 'documentTitle') ||
    hasKey(header, 'invoiceNumber') ||
    hasKey(footer, 'invoiceNumber') ||
    freeTextCoversToken(header, 'documentTitle') ||
    freeTextCoversToken(header, 'invoiceNumber') ||
    freeTextCoversToken(footer, 'invoiceNumber')
  );
}

export function validateDocumentLayout(layout: unknown): LayoutValidationResult {
  const issues: LayoutValidationIssue[] = [];

  if (!isDocumentLayoutV1(layout)) {
    return {
      ok: false,
      issues: [
        {
          code: 'invalid_shape',
          message:
            'Das Layout hat ein ungültiges Format (erwartet Version 1 mit Kopf-, Fuß- und Body-Bereich).',
        },
      ],
    };
  }

  const { header, footer, body } = layout;

  if (!isFiniteNumber(header.heightPt) || header.heightPt < 40 || header.heightPt > PDF_A4_HEIGHT_PT - 120) {
    issues.push({
      code: 'invalid_band_height',
      message: `Die Kopfbereich-Höhe ist ungültig (${String(header.heightPt)} pt).`,
    });
  }
  if (!isFiniteNumber(footer.heightPt) || footer.heightPt < 24 || footer.heightPt > 200) {
    issues.push({
      code: 'invalid_band_height',
      message: `Die Fußbereich-Höhe ist ungültig (${String(footer.heightPt)} pt).`,
    });
  }
  if (!isFiniteNumber(body.fontSizePt) || body.fontSizePt < 7 || body.fontSizePt > 14) {
    issues.push({
      code: 'invalid_shape',
      message: 'Die Schriftgröße im Body muss zwischen 7 und 14 pt liegen.',
    });
  }
  if (body.pageMarginsPt !== undefined) {
    const margins = body.pageMarginsPt;
    const values =
      typeof margins === 'object' && margins !== null
        ? [margins.top, margins.right, margins.bottom, margins.left]
        : [];
    const validValues = values.every((value) => isFiniteNumber(value) && value >= 0 && value <= 200);
    const validWritingArea =
      values.length === 4 &&
      validValues &&
      margins.left + margins.right <= PDF_A4_WIDTH_PT - 100 &&
      margins.top + margins.bottom <= PDF_A4_HEIGHT_PT - 120;
    if (!validWritingArea) {
      issues.push({
        code: 'invalid_page_margins',
        message: 'Die Seitenränder sind ungültig oder lassen zu wenig Platz für den Dokumentinhalt.',
      });
    }
  }

  for (const el of header.elements) {
    if (!HEADER_KEY_SET.has(el.key)) {
      issues.push({
        code: 'unknown_key',
        message: `Unbekanntes Element im Kopfbereich: „${elementLabel(el.key)}“.`,
        elementId: el.id,
        key: el.key,
      });
    }
    if (elementOutOfBounds(el, header.heightPt)) {
      issues.push({
        code: 'out_of_bounds',
        message: `„${elementLabel(el.key)}“ liegt außerhalb des Kopfbereichs (Position oder Größe anpassen).`,
        elementId: el.id,
        key: el.key,
      });
    }
  }

  for (const el of footer.elements) {
    if (!FOOTER_KEY_SET.has(el.key)) {
      issues.push({
        code: 'unknown_key',
        message: `Unbekanntes Element im Fußbereich: „${elementLabel(el.key)}“.`,
        elementId: el.id,
        key: el.key,
      });
    }
    if (elementOutOfBounds(el, footer.heightPt)) {
      issues.push({
        code: 'out_of_bounds',
        message: `„${elementLabel(el.key)}“ liegt außerhalb des Fußbereichs (Position oder Größe anpassen).`,
        elementId: el.id,
        key: el.key,
      });
    }
  }

  for (const key of MANDATORY_HEADER_KEYS) {
    if (key === 'documentTitle') {
      if (!hasInvoiceNumberCoverage(layout)) {
        issues.push({
          code: 'missing_mandatory',
          message: 'Dokumenttitel oder Dokumentnummer ist Pflicht (§14 UStG).',
          key: 'documentTitle',
        });
      }
      continue;
    }
    if (!hasKey(header.elements, key)) {
      // Token-coverable meta fields (e.g. after Zusammenführen into freeText).
      if (
        (key === 'issueDate' || key === 'leistungszeitraum') &&
        hasFieldOrTokenCoverage(header.elements, key)
      ) {
        continue;
      }
      issues.push({
        code: 'missing_mandatory',
        message: `Pflichtfeld „${elementLabel(key)}“ fehlt im Kopfbereich.`,
        key,
      });
    }
  }

  for (const key of MANDATORY_FOOTER_KEYS) {
    if (!hasKey(footer.elements, key)) {
      issues.push({
        code: 'missing_mandatory',
        message: `Pflichtfeld „${elementLabel(key)}“ fehlt im Fußbereich.`,
        key,
      });
    }
  }

  // Überlappungen sind bewusst erlaubt (z. B. Absenderzeile knapp über dem Empfängerfeld).
  return { ok: issues.length === 0, issues };
}

/** Ensure mandatory elements cannot be removed by editor actions. */
export function isMandatoryElementKey(key: LayoutElementKey, band: 'header' | 'footer'): boolean {
  if (key === 'documentTitle' || key === 'invoiceNumber') return true;
  if (band === 'header') {
    return (MANDATORY_HEADER_KEYS as readonly string[]).includes(key);
  }
  return (MANDATORY_FOOTER_KEYS as readonly string[]).includes(key);
}

export function canRemoveElement(
  layout: DocumentLayoutV1,
  band: 'header' | 'footer',
  elementId: string,
): boolean {
  const elements = band === 'header' ? layout.header.elements : layout.footer.elements;
  const el = elements.find((e) => e.id === elementId);
  if (!el) return false;
  if (!isMandatoryElementKey(el.key, band)) return true;

  const remaining = elements.filter((e) => e.id !== elementId);

  // documentTitle can be removed only if invoiceNumber remains (and vice versa),
  // including a copy in the other band or a covering freeText token.
  if (el.key === 'documentTitle' || el.key === 'invoiceNumber') {
    const next: DocumentLayoutV1 = {
      ...layout,
      header: {
        ...layout.header,
        elements: band === 'header' ? remaining : layout.header.elements,
      },
      footer: {
        ...layout.footer,
        elements: band === 'footer' ? remaining : layout.footer.elements,
      },
    };
    return hasInvoiceNumberCoverage(next);
  }

  // Meta Pflichtfelder dürfen weg, wenn ein Freitext denselben Platzhalter enthält.
  if (el.key === 'issueDate' || el.key === 'leistungszeitraum') {
    return hasFieldOrTokenCoverage(remaining, el.key);
  }

  return false;
}
